"use client"

import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, useInViewConfig, useTransitionUpStyles } from "ui"
import CommonTitle from "ui/common/bit/CommonTitle"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getUmbracoMediaUrl } from "utils"
import BlockWrapper from "../common/BlockWrapper"
import VideoContainer from "./VideoContainer"

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  gap: ${sizes.baseDouble};

  ${mediaQueries.mobile} {
    gap: ${sizes.baseTriple};
  }

  ${mediaQueries.tablet} {
    gap: ${sizes.baseTriple};
  }

  ${mediaQueries.desktopM} {
    gap: ${sizes.baseQuadric};
  }
`

const StyledImageContainer = styled(Box)`
  position: relative;
  width: 100%;
  border-radius: 30px;
  overflow: hidden;
  aspect-ratio: 16 / 9;

  ${mediaQueries.mobile} {
    border-radius: 12px;
  }

  ${mediaQueries.tablet} {
    border-radius: 12px;
  }
`

const StyledImage = styled(Image)`
  object-fit: cover;
`

const StyledText = styled(Typography)`
  text-align: center;
  width: 100%;
  max-width: 800px;
  margin-bottom: ${sizes.baseSingle};
`

interface ImageAndVideoBlockProps {
  data: any
  id?: string
}

const ImageAndVideoBlock = ({ data, id }: ImageAndVideoBlockProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView, transitionDelayTime)

  // Extract all properties from data with optional chaining for undefined safety
  const title = data?.title?.value
  const subtitle = data?.subtitle?.value
  const imageDescription = data?.description?.value
  const image = data?.image?.mediaItems?.[0]
  const { url: imageUrl = "", altText = "" } = image || {}
  const video = data?.video?.mediaItems?.[0] || data?.video?.links?.[0]

  // Get the image URL safely
  const safeImageUrl = imageUrl ? getUmbracoMediaUrl(imageUrl) : ""

  return (
    <BlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        {imageUrl && safeImageUrl && (
          <StyledImageContainer>
            <StyledImage src={safeImageUrl} alt={altText || ""} fill priority />
          </StyledImageContainer>
        )}

        {imageDescription && (
          <StyledText variant="body1" color={colors.black}>
            {imageDescription}
          </StyledText>
        )}

        <CommonTitle title={title} subtitle={subtitle} />

        {video?.url && <VideoContainer url={video?.url} title={title} />}
      </StyledContainer>
    </BlockWrapper>
  )
}

export default ImageAndVideoBlock
